import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Button, Input, LoadingIcon, Typography } from '@/primer/components';
import { PageLayout } from '@/components/layout';
import { useInvite } from '@/api';
import { inviteEmailSchema } from '@/schemas';

const HELPER_TEXT = "We'll send an OTP to this email address";

export function InviteEmail() {
  const { token } = useSearch({ from: '/_public/invite' });
  const { data: invite, isLoading, error } = useInvite(token || '');

  const [typedEmail, setTypedEmail] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="col-span-12 flex justify-center py-8">
        <LoadingIcon size="large" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
        <div className="p-6 space-y-2 text-center">
          <Typography variant="header" size="h2" color="error">
            Invalid Invitation
          </Typography>
          <Typography variant="body" size="small" color="bodylighter" align="center" as="p">
            This invitation link is invalid or has expired.
          </Typography>
        </div>
      </PageLayout>
    );
  }
  const knownEmail = invite.email ?? '';
  const isReturningCreator = Boolean(invite.hasSignedInBefore) && knownEmail !== '';
  const email = typedEmail ?? knownEmail;
  const canContinue = isReturningCreator || inviteEmailSchema.safeParse({ email }).success;
  return (
    <PageLayout
      className="flex flex-col flex-1 justify-between py-8"
      gridProps={{ className: 'px-5 flex-1' }}
    >
      <div className="flex flex-col gap-1">
        {isReturningCreator ? (
          <Input
            label="Email address"
            type="email"
            value={knownEmail}
            readOnly
            helperText={HELPER_TEXT}
            data-testid="invite-email-input"
          />
        ) : (
          <Input
            label="Email address"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => setTypedEmail(event.target.value)}
            helperText={HELPER_TEXT}
            data-testid="invite-email-input"
          />
        )}

        <div className="mt-14 space-y-4">
          {/* TODO - PD-324734: Add Terms & Conditions / Privacy Policy as actual URLs once provided. */}
          {!isReturningCreator && (
            <Typography variant="body" size="small" color="body" align="center" as="p">
              By signing up, you agree to our{' '}
              <Typography variant="body" size="small" color="interactive" as="span">
                Terms &amp; Conditions
              </Typography>{' '}
              and{' '}
              <Typography variant="body" size="small" color="interactive" as="span">
                Privacy Policy
              </Typography>
            </Typography>
          )}

          {/* TODO: request OTP and navigate to verification screen on Next. */}
          <Button
            variant="primary"
            size="large"
            fullWidth
            disabled={!canContinue}
            data-testid="invite-email-next-button"
          >
            Next
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}

export default InviteEmail;
